import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './FirstTradeConfirmModal.css';

interface Props {
  isOpen: boolean;
  /** `dontShowAgain` is the checkbox below the buttons (Asana 1219704178739168). */
  onConfirm: (dontShowAgain: boolean) => void;
  onCancel: () => void;
  isCompletingTrade: boolean;
}

const FirstTradeConfirmModal: React.FC<Props> = ({
  isOpen,
  onConfirm,
  onCancel,
  isCompletingTrade,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // The box is unchecked every time the modal opens; a cancel never carries the
  // choice over to the next completion.
  useEffect(() => {
    if (!isOpen) setDontShowAgain(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return createPortal(
    <div
      className="first-trade-confirm-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-trade-confirm-title"
    >
      <div className="first-trade-confirm-modal">
        <div className="first-trade-confirm-icon">📬</div>

        <h2 id="first-trade-confirm-title" className="first-trade-confirm-title">
          Did your cards arrive safely?
        </h2>
        <p className="first-trade-confirm-subtitle">
          Before you complete this trade, take a moment to verify.
        </p>

        <div className="first-trade-confirm-checklist">
          <div className="first-trade-confirm-check-item">
            <span className="first-trade-confirm-check-icon">🃏</span>
            <div>
              <strong>Correct edition?</strong> Make sure the set and printing match what was agreed.
            </div>
          </div>
          <div className="first-trade-confirm-check-item">
            <span className="first-trade-confirm-check-icon">✨</span>
            <div>
              <strong>Correct condition?</strong> Cards should match the condition listed on the trade.
            </div>
          </div>
          <div className="first-trade-confirm-check-item">
            <span className="first-trade-confirm-check-icon">🌐</span>
            <div>
              <strong>Correct language?</strong> Confirm the language matches your preference.
            </div>
          </div>
        </div>

        <p className="first-trade-confirm-warning">
          Completing a trade releases points to your sender. This action cannot be undone.
        </p>

        <div className="first-trade-confirm-actions">
          <button
            className="first-trade-confirm-btn-primary"
            onClick={() => onConfirm(dontShowAgain)}
            disabled={isCompletingTrade}
          >
            {isCompletingTrade ? 'Completing...' : 'Yes, my cards arrived correctly'}
          </button>
          <button
            className="first-trade-confirm-btn-secondary"
            onClick={onCancel}
            disabled={isCompletingTrade}
          >
            Not yet — I'll check first
          </button>
        </div>

        <label className="first-trade-confirm-dismiss">
          <input
            type="checkbox"
            className="first-trade-confirm-dismiss-input"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            disabled={isCompletingTrade}
          />
          <span className="first-trade-confirm-dismiss-box" aria-hidden="true" />
          <span className="first-trade-confirm-dismiss-label">Don't show this again</span>
        </label>
      </div>
    </div>,
    document.body
  );
};

export default FirstTradeConfirmModal;
